/**
 * Display helpers for Boost ledger rows. Amounts are signed: debits and expiries are negative.
 */

import type { WorkspaceBoostHistoryResponse, BoostWorkflowUsed } from "../services/naraDoor";

const LEDGER_TYPE_KEYS: Record<string, string> = {
  expiry: "billing.ledger.types.expiry",
  contract_topup: "billing.ledger.types.contract_topup",
};

export function signedBoosts(amount: number): string {
  const sign = amount > 0 ? "+" : amount < 0 ? "−" : "";
  return `${sign}${Math.abs(amount).toLocaleString()}`;
}

/** i18n key for a ledger type that has a label; undefined keeps the existing label. */
export function ledgerTypeKey(type: string): string | undefined {
  return LEDGER_TYPE_KEYS[type];
}

/** Transcription workflows, keyed by the speech model their debit rows carry. */
export const STT_WORKFLOWS = [
  { model: "chirp_3", label: "Standard (Chirp 3)" },
  { model: "gemini-3.5-transcribe-preview", label: "Gemini Transcribe" },
] as const;

/** Every ledger type nara-core writes. */
export const BOOST_TRANSACTION_TYPES = [
  "usage", "purchase", "subscription_credit", "auto_topup", "bonus", "admin_grant", "expiry", "contract_topup",
] as const;

/** Label of the workflow a row was billed for; "—" when the row has no speech model. */
export function sttWorkflowLabel(model: string | null | undefined): string {
  if (!model) return "—";
  return STT_WORKFLOWS.find((w) => w.model === model)?.label ?? model;
}

/** Workflow column: the server's label for the row's flow, else the speech model's label. */
export function txWorkflowLabel(workflowLabel: string | null | undefined, sttModel: string | null | undefined): string {
  return workflowLabel ?? sttWorkflowLabel(sttModel);
}

/** "document_generation:<uuid>" -> "Document Generation"; "—" when the row has no feature. */
export function formatFeatureName(feature: string | undefined): string {
  if (!feature || feature === "unknown") return "—";
  // Strip trailing UUID (e.g. "document_processing:6aa9bc61-...")
  const name = feature.replace(/:[0-9a-f]{8}-[0-9a-f]{4}-.*$/i, "");
  return name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Description column: a usage row names its feature; other types keep their own description. */
export function txDescription(tx: { type: string; description?: string; feature?: string }): string {
  return tx.type !== "usage" ? tx.description || formatFeatureName(tx.feature) : formatFeatureName(tx.feature);
}

/** Offset of the next page: rows already loaded, or undefined when the last page was the end. */
export function nextHistoryOffset(pages: WorkspaceBoostHistoryResponse[]): number | undefined {
  const last = pages[pages.length - 1];
  if (!last?.hasMore) return undefined;
  return pages.reduce((n, p) => n + p.transactions.length, 0);
}

/** One select value per (workflowType, transcriptionWorkflow) pair the workspace used. */
export function workflowKey({ workflowType, transcriptionWorkflow }: Pick<BoostWorkflowUsed, "workflowType" | "transcriptionWorkflow">): string {
  return `${workflowType ?? ""}|${transcriptionWorkflow ?? ""}`;
}

export function workflowParams(key: string): { workflowType?: string; transcriptionWorkflow?: string } {
  const [workflowType, transcriptionWorkflow] = key.split("|");
  return { workflowType: workflowType || undefined, transcriptionWorkflow: transcriptionWorkflow || undefined };
}

/** Select options for the workflows used; an entry with no workflow type cannot be filtered on, so it is left out. */
export function workflowOptions(used: BoostWorkflowUsed[] | undefined): { value: string; label: string }[] {
  return (used ?? []).filter((w) => w.workflowType).map((w) => ({ value: workflowKey(w), label: w.label }));
}

/** m:ss; "—" when the flow has no audio. */
export function formatAudioDuration(sec: number | null | undefined): string {
  if (sec == null) return "—";
  const total = Math.round(sec);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

export interface TxFilters {
  type: string;
  userId: string;
  /** workflowKey of the chosen workflow; empty for all. */
  workflow: string;
  startDate: string;
  endDate: string;
}

export const EMPTY_TX_FILTERS: TxFilters = { type: "", userId: "", workflow: "", startDate: "", endDate: "" };

/** A changed filter shows a different result set, so paging restarts at the first page. */
export function applyTxFilter(
  state: { filters: TxFilters; offset: number },
  patch: Partial<TxFilters>
): { filters: TxFilters; offset: number } {
  return { filters: { ...state.filters, ...patch }, offset: 0 };
}
